import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["tableComponent", "announceLiveText", "controlled", "customStrings", "data", "disabledRows", "onSelectedChange", "onSelectedAllChange", "paginatedData", "rowAriaLabel", "rowClassName", "retainStateOnDataChange", "selectAll", "selectionType", "selectedRows", "selectAllConfiguration", "totalRows"],
      _excluded2 = ["selectable", "data", "retainStateOnDataChange"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import memoize from "micro-memoize";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import Checkbox from "../../Checkbox/Checkbox";
import { useUniqueId } from "../../common/hooks";
import { getObjectKeyFn, getRowKeyText, resultOf } from "../../common/utilities";
import LiveRegion from "../../private/LiveRegion/LiveRegion";
import SelectAll from "../../private/SelectAll/SelectAll";
import SELECTALL_STRINGS from "../../private/SelectAll/strings";
import { CLEAR_ANNOUNCEMENT_TIMEOUT, SELECTABLE_COLUMN_KEY, SELECTABLE_ROW_CLASS } from "../constants";
import { getExistingRowsCount } from "../utils";
import extractTheme from "./extractTheme";
import SelectableRow from "./SelectableRow";
import SelectionControl from "./SelectionControl";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const SINGLE_SELECTION = "single";
const ROW_SELECTION = "row";
/**
 * `withSelectable` is an HOC that adds [Checkboxes](#checkbox) and
 * [RadioButtons](#radiobutton) to [Table](#table) rows for the purpose of
 * selecting/unselecting data sets, which can be communicated to a remote service.
 * @type {Component}
 */

const Selectable = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    tableComponent: TableComponent,
    announceLiveText,
    // eslint-disable-line no-unused-vars
    controlled,
    // eslint-disable-line react/prop-types
    customStrings,
    data,
    disabledRows,
    onSelectedChange,
    onSelectedAllChange,
    // eslint-disable-line no-unused-vars
    paginatedData,
    // eslint-disable-line react/prop-types
    rowAriaLabel,
    rowClassName,
    // eslint-disable-line react/prop-types
    retainStateOnDataChange,
    // eslint-disable-line no-unused-vars
    selectAll,
    selectionType,
    selectedRows,
    // eslint-disable-line no-unused-vars
    selectAllConfiguration,
    // eslint-disable-line no-unused-vars
    totalRows
  } = props,
        tableProps = _objectWithoutProperties(props, _excluded);

  const internalRef = useRef();
  const tableRef = ref || internalRef;
  const theme = useTheme();
  const {
    selectableCSSStyles
  } = extractTheme(_objectSpread({
    theme
  }, tableProps));
  const {
    selectableStyle,
    selectableRowStyle,
    selectionTypeStyle,
    singleSelectionStyle
  } = selectableCSSStyles;
  /**
   * This is a passthrough variable to `SelectAll` component.
   * We need this variable to add selected styles to menu items in `SelectAll`.
   */

  let selectedValue = "";
  const selectAllCheckbox = useRef();
  const selectedCheckbox = useUniqueId("pnr-table-");
  const headerColumnId = useUniqueId("pnr-table__header-");
  /**
   * Instance variable used to determine if `selectAllConfiguration` is set to "checkbox".
   *
   * @returns Returns a boolean.
   */

  const selectAllConfigIsCheckbox = selectAllConfiguration && selectAllConfiguration.selectAllType && selectAllConfiguration.selectAllType.toLowerCase() === "checkbox";
  const [liveRegionText, setLiveRegionText] = useState("");
  /**
   * Infer the intended state of the SelectAll component.
   *
   * @returns Returns a string which correlates to the intended
   *          SelectAll component state.
   */

  const inferSelectAllState = memoize((totalSelectedRowsOnPage, totalAvailableRows, totalSelectedRows, totalRows) => {
    if (totalSelectedRows === totalRows) {
      // Select all items.
      return SELECTALL_STRINGS.selectAllItems;
    } else if (totalSelectedRowsOnPage === totalAvailableRows) {
      // Select all on this page.
      return SELECTALL_STRINGS.selectAllOnThisPage;
    } else if (totalSelectedRowsOnPage === 0 && totalSelectedRowsOnPage !== totalSelectedRows) {
      // Unselect all on this page.
      return SELECTALL_STRINGS.unselectAllOnThisPage;
    } else if (totalSelectedRows === 0) {
      // Unselect all items.
      return SELECTALL_STRINGS.unselectAllItems;
    } else {
      return "";
    }
  });
  /**
   * Filters common values found in both the
   * `selectedRows` and `disabledRows` props.
   *
   * @return {Array} Unique values of the `selectedRows` prop.
   */

  const getEnabledSelectedRows = () => {
    return selectedRows.filter(rowId => !disabledRows.includes(rowId));
  };
  /**
   * It resolves the rowKey value.
   * @param  {Object} item Row's item
   * @return {*}
   */


  const getRowKeyValue = getObjectKeyFn(props.rowKey, "rowKey");
  /**
   * Used to determine if the column header Checkbox is indeterminate.
   *
   * @param {*} Object Destructures data and selected items within the arguments.
   */

  const isSelectAllIndeterminate = () => {
    if (paginatedData.length && selectAllConfigIsCheckbox) {
      const paginatedDataIds = paginatedData.map(row => {
        const itemKey = getRowKeyValue(row);
        return itemKey;
      });

      if (paginatedDataIds.every(id => selectedRows.includes(id))) {
        return false;
      }

      return paginatedDataIds.some(id => selectedRows.indexOf(id) >= 0);
    } else {
      return selectedRows.length > 0 && getEnabledSelectedRows().length < data.length - disabledRows.length;
    }
  };

  useEffect(() => {
    /**
     * Add the HTML indeterminate attribute.
     *
     * When a checkbox is selected and disabled, this logic ensures that the "Select All"
     * component will maintain an indeterminate state.
     */
    if (selectAllCheckbox.current && isSelectAllIndeterminate()) {
      selectAllCheckbox.current.indeterminate = true;
    }
  });
  /**
   * Gets the rows currently selected on the page. This method does
   * distinguish between disabled and selected vs enabled and
   * selected. It returns all selected rows on the page
   * excluding disabled rows.
   *
   * @returns Returns an array of selected rows on the current page.
   */

  const getRowsSelectedOnPage = () => {
    const enabledSelectedRows = getEnabledSelectedRows();
    const totalSelectedRowsOnPage = [];
    paginatedData.forEach(n => {
      const itemKey = getRowKeyValue(n); // If rowKey exists is found in selectedRows (disabled or enabled).

      if (enabledSelectedRows.indexOf(itemKey) > -1) {
        totalSelectedRowsOnPage.push(itemKey);
      }
    });
    return totalSelectedRowsOnPage;
  };
  /**
   * Get the Accessibility label for a given input.
   *
   * @returns String
   */


  const getA11YLabel = (item, itemKey, selected) => {
    if (rowAriaLabel) {
      return rowAriaLabel({
        item,
        selected
      });
    }

    return getRowKeyText(itemKey, customStrings.row);
  };
  /**
   * Reconciles selected data with the selectAll action.
   *
   * @param {Boolean} [acrossAllPages] Boolean indicating whether pagination should be considered
   *                  in the logic. For example, when considering the state of selections
   *                  of data across all pages, the logic should not be bound by pagination
   *                  concerns.
   * @param {Boolean} [addSelectedRows] Boolean indicating whether existing selected rows
   *                  should be added to the total selected rows.
   * @returns Object Returns an object of arrays: totalSelectedRows and disabledAndSelected.
   */


  const reconcileSelected = (acrossAllPages, addSelectedRows) => {
    const disabledAndSelected = [];
    const totalSelectedRows = [];
    let pageData = acrossAllPages && paginatedData.length ? paginatedData : data;

    if (selectAllConfigIsCheckbox && paginatedData.length) {
      pageData = paginatedData;
    }

    pageData.forEach(n => {
      const itemKey = getRowKeyValue(n);

      if ( // If rowKey exists in disabledRows and is also in selectedRows.
      disabledRows.indexOf(itemKey) > -1 && selectedRows.indexOf(itemKey) > -1) {
        disabledAndSelected.push(itemKey);
        return false;
      } else if ( // If rowKey exists in disabledRows and not in selectedRows.
      disabledRows.indexOf(itemKey) > -1 && selectedRows.indexOf(itemKey) < 0) {
        return false;
      }

      totalSelectedRows.push(itemKey);
    }); // This step is only to be done when a paginator is present.

    if (paginatedData.length && (acrossAllPages || addSelectedRows)) {
      totalSelectedRows.push(...selectedRows);
    }

    return {
      totalSelectedRows,
      disabledAndSelected
    };
  };
  /**
   * Select all rows, taking into account disabled rows.
   *
   * @returns {Array} An array of all selected rows.
   */


  const selectAllRows = acrossAllPages => {
    const reconciledItems = reconcileSelected(acrossAllPages, true); // Return an array without duplicate rows.

    return [...new Set([...reconciledItems.totalSelectedRows, ...reconciledItems.disabledAndSelected])];
  };
  /**
   * This method returns the number of disabled records
   * in the current page.
   * @param {Array} pageData Current page data.
   *
   * @return {Number} Total count of disabled records.
   */


  const getDisabledRows = pageData => {
    return pageData.filter(row => {
      const itemKey = getRowKeyValue(row);
      return disabledRows.indexOf(itemKey) > -1;
    }).length;
  };
  /**
   * Used to resolve the "Select All" checkbox's intended state.
   * The challenge is that the "Select All" checkbox must visually indicate an
   * indeterminate state whenever disabled checkboxes are part of the data set.
   * Default Chrome/FF checkbox behavior circularly traverses the following states
   * in a browser:
   * "unchecked" --> "indeterminate" (if used) --> "checked".  This means that whenever
   * a data set includes an unchecked-disabled checkbox, and all other checkboxes
   * are selected, that "Select All" will always be indeterminate.  Since the state
   * following indeterminate is "checked=true"; consequently, the unselectAll logic
   * never fires.
   * Summary:
   * By tying the "Select All" checkbox to a selectAllIsChecked(), the logic can avoid
   * getting locked into its HTML state.
   *
   * @param {Boolean} [acrossAllPages] Boolean indicating whether pagination should be
   *                  considered in the logic. For example, when considering the state of
   *                  selections of data across all pages, the logic should not be bound
   *                  by pagination concerns.
   */


  const selectAllIsChecked = acrossAllPages => {
    /**
     * The state of the "Select All" checkbox should be based on the selected
     * rows that are part of the current data because a filter may have been
     * applied and some of the rows in selectedRows may not be present.
     */
    const dataIds = data.map(item => getRowKeyValue(item));
    const visibleSelectedRows = selectedRows.filter(id => dataIds.indexOf(id) > -1);
    const hasSelectedRows = visibleSelectedRows.length > 0;
    /* istanbul ignore next */

    const numberOfRows = controlled ? totalRows : data.length;
    const pageData = acrossAllPages && paginatedData.length ? paginatedData : data;
    const totalRowsThatShouldNotBeToggled = reconcileSelected(acrossAllPages).disabledAndSelected.length;
    let totalRowsDisabled = disabledRows.length,
        totalRowsSelected = selectedRows.length,
        totalRowsOnPage = pageData.length;
    /**
     * Ensure Checkbox header is not checked under the following conditions:
     * - Select All Type is `checkbox`.
     * - All items are disabled and not selected.
     */

    if (!paginatedData.length && !selectAllConfigIsCheckbox) {
      if (numberOfRows > 0 && hasSelectedRows) {
        const {
          disabledAndSelected
        } = reconcileSelected(acrossAllPages);
        const selectedAndNotDisabledCount = visibleSelectedRows.length - disabledAndSelected.length;
        const notDisabledRowCount = numberOfRows - disabledRows.length;
        return selectedAndNotDisabledCount === notDisabledRowCount;
      } else {
        return false;
      }
    } else {
      /*
       * // This step is only to be done when a paginator is present.
       *
       * // Evaluate `selectedRows` available on the current page.
       */
      totalRowsSelected = pageData.filter(row => {
        const itemKey = getRowKeyValue(row);
        return visibleSelectedRows.indexOf(itemKey) > -1;
      }).length;
      totalRowsDisabled = getDisabledRows(pageData);
    }

    if (selectAllConfigIsCheckbox) {
      totalRowsOnPage = paginatedData.length;
      totalRowsSelected = paginatedData.filter(row => {
        const itemKey = getRowKeyValue(row);
        return selectedRows.indexOf(itemKey) > -1;
      }).length;
      totalRowsDisabled = getDisabledRows(paginatedData);
      /* istanbul ignore else */

      if (totalRowsDisabled === totalRowsOnPage) {
        if (totalRowsOnPage === totalRowsSelected) {
          return true;
        }

        return false;
      }
    }

    return totalRowsOnPage > 0 && totalRowsSelected - totalRowsThatShouldNotBeToggled === totalRowsOnPage - totalRowsDisabled;
  };
  /**
   * Unselect all rows, taking into account disabled rows.
   *
   * @returns {Array} An array of all unselected rows.
   */


  const unselectAll = acrossAllPages => {
    const {
      totalSelectedRows,
      disabledAndSelected
    } = reconcileSelected(acrossAllPages);
    let rowsToKeep = [];
    /**
     * When the Paginator is used, it is important to remove data
     * from the current page only.
     */

    if (paginatedData.length && acrossAllPages) {
      const rowsOnCurrentPage = paginatedData.map(row => {
        const itemKey = getRowKeyValue(row);
        return itemKey;
      });
      rowsToKeep = totalSelectedRows.filter(index => rowsOnCurrentPage.indexOf(index) === -1);
    } else if (retainStateOnDataChange) {
      /**
       * Condition: considering table data is filtered when retainStateOnDataChange is passed.
       * When the table is filtered and `Unselect all items` is clicked, unselecting only records
       * from the filtered data set only.
       */
      rowsToKeep = selectedRows.filter(index => totalSelectedRows.indexOf(index) === -1);
    }

    return [...new Set([...disabledAndSelected, ...rowsToKeep])];
  };
  /**
   * Handle results of selection from the header component.
   */


  const handleSelectionAll = selectAll => {
    const {
      selectedRows: previouslySelectedRows
    } = props;
    let selectedRows = [];

    if (selectAll) {
      selectedRows = unselectAll(true);
    } else {
      selectedRows = selectAllRows(true);
    }

    const changedRowsCount = Math.abs(previouslySelectedRows.length - selectedRows.length);
    /* istanbul ignore else */

    if (changedRowsCount > 0) {
      const selectAllLiveText = customStrings.getSelectAllStatus(changedRowsCount, !selectAll ? customStrings.selected : customStrings.deselected);
      setLiveRegionText(selectAllLiveText);
    }

    onSelectedChange({
      selectedRows
    });
  };
  /**
   * Toggles page selection for the Checkbox.
   */


  const handleSelectAllFromCheckbox = () => {
    handleSelectionAll(selectAllIsChecked(false));
  };
  /**
   * Handler that is executed on every selection made in `SelectAll` menu.
   *
   * @param {String} selectedValue The value of menu item selected.
   */


  const handleSelectedValueChange = selectedChagedValue => {
    selectedValue = selectedChagedValue;
  }; // Unselects all rows on the current page.


  const handleUnselectAllOnPage = () => {
    handleSelectionAll(true);
  }; // Selects all rows on the current page.


  const handleSelectAllOnPage = () => {
    handleSelectionAll(false);
  }; // Unselects all rows across the data set.


  const handleUnselectAllAcrossPages = () => {
    /* istanbul ignore else */
    if (selectedRows.length > 0) {
      const updatedLiveText = customStrings.getSelectAllStatus(selectedRows.length, customStrings.deselected);
      setLiveRegionText(updatedLiveText);
    }
    /**
     * If this is a controlled Table, then invoke the
     * `onSelectedAllChange` callback. This allows the
     * application code to handle deselection across all
     * pages logic.
     */


    if (controlled) {
      onSelectedAllChange(false);
      return;
    }
    /**
     * The parameter passed to unselectAll sets the logic
     * to disregard paginated data, and work across the
     * entire set.
     */


    const newSelectedRows = [...unselectAll(false)];
    onSelectedChange({
      selectedRows: newSelectedRows
    });
  };
  /**
   * Selects all rows across the data set.
   */


  const handleSelectAllAcrossPages = () => {
    let rowsToSubstract = selectedRows.length;

    if (retainStateOnDataChange) {
      /**
       * If this is a filtered data set, the number of newly selected rows
       * is equal to the total number of rows in the filtered data set minus
       * the number of rows in it which are already selected.
       */
      const dataIds = data.map(item => item.id);
      rowsToSubstract = selectedRows.filter(row => dataIds.indexOf(row) > -1).length;
    }

    const rowsToSelectCount = Math.abs(totalRows - rowsToSubstract);
    /* istanbul ignore else */

    if (rowsToSelectCount > 0) {
      const selectedRowCount = customStrings.getSelectAllStatus(rowsToSelectCount, customStrings.selected);
      setLiveRegionText(selectedRowCount);
    }
    /**
     * If this is a controlled Table, then invoke the
     * `onSelectedAllChange` callback. This allows the
     * application code to handle selection across all
     * pages logic.
     */


    if (controlled) {
      onSelectedAllChange(true);
      return;
    }
    /**
     * The parameter passed to selectAll sets the logic
     * to disregard paginated data, and work across the
     * entire set.
     */


    const newSelectedRows = [...selectAllRows(false)];
    onSelectedChange({
      selectedRows: newSelectedRows
    });
  };
  /**
   * Returns the selectable column header with the appropriate HTML:
   * checkbox or nothing at all.
   * @param {Number} totalRowSelections Selected rows count.
   * @param {String} selectedValue Menu item selected in `SelectAll`.
   */


  const getHeader = (totalRowsSelected, selectedValue) => {
    const isIndeterminate = isSelectAllIndeterminate();
    const isSelectAllChecked = selectAllIsChecked(true);
    const isSelected = isIndeterminate ? false : isSelectAllChecked;

    if (selectAll) {
      if (!paginatedData.length) {
        // Return a selectAll Checkbox component to the column head.
        return _jsx(Checkbox, {
          checked: isSelected,
          disabled: data.length === disabledRows.length,
          ref: selectAllCheckbox,
          onValueChange: handleSelectAllFromCheckbox,
          indeterminate: isIndeterminate,
          "aria-label": customStrings.selectAll,
          label: "",
          labelHidden: true,
          name: customStrings.selectAll,
          css: selectionTypeStyle
        });
      } else {
        const disabledRowsLength = disabledRows.length;

        if (selectAllConfigIsCheckbox) {
          const paginatedDataIds = paginatedData.map(row => {
            const itemKey = getRowKeyValue(row);
            return itemKey;
          });
          const isSelectAllDisabled = paginatedDataIds.every(id => disabledRows.includes(id)); // Return a selectAll Checkbox component to the column head.

          return _jsx(Checkbox, {
            checked: isSelected,
            disabled: isSelectAllDisabled,
            ref: selectAllCheckbox,
            onValueChange: handleSelectAllFromCheckbox,
            indeterminate: isIndeterminate,
            "aria-label": customStrings.selectAll,
            label: "",
            labelHidden: true,
            name: customStrings.selectAll
          });
        }

        return _jsx(SelectAll, {
          handleDeselectAllItemsOnPage: handleUnselectAllOnPage,
          handleSelectAllItemsOnPage: handleSelectAllOnPage,
          handleDeselectAllAcrossPages: handleUnselectAllAcrossPages,
          handleSelectAllAcrossPages: handleSelectAllAcrossPages,
          totalItems: props.totalRows - disabledRowsLength,
          selectedValue: selectedValue,
          onSelectedValueChange: handleSelectedValueChange // When controlled, App need to update from Table.
          ,
          totalItemsSelectedAcrossAllPages: totalRowsSelected,
          customStrings: customStrings
        });
      }
    } // Column does not have a column selection component in the column head.


    return _jsx("span", {
      className: "wf-u-visually-hidden",
      children: customStrings.select
    });
  };
  /**
   * Handle row selections.
   * @param {*} id The rowKey id of the item (i.e. data[rowkey value])
   * @param {Boolean} isRowSelected A boolean value indicating whether
   *                                the given row is selected.
   */


  const handleSelection = (id, isRowSelected) => {
    const currentSelectedRows = [...selectedRows];
    const singleSelection = selectionType === SINGLE_SELECTION;
    let totalRowSelections = [];

    if (isRowSelected) {
      // Select row.
      totalRowSelections = singleSelection ? [id] : currentSelectedRows.concat(id);
    } else {
      // Unselect row.
      currentSelectedRows.splice(currentSelectedRows.indexOf(id), 1);
      totalRowSelections = currentSelectedRows;
    }

    if (selectionType === ROW_SELECTION) {
      const updatedText = isRowSelected ? customStrings.selected :
      /* istanbul ignore next */
      customStrings.deselected;
      announceLiveText && announceLiveText(updatedText);
    }

    onSelectedChange({
      selectedRows: totalRowSelections
    });
  };
  /**
   * Get props to be returned in Table's `bodyRowComponentProps` prop.
   * @param Object Takes an object and expects to destruct `itemKey`.
   * @returns Returns an object of properties describing the state of the row.
   */


  const getBodyRowComponentProps = extraProps => {
    const {
      bodyRowComponent,
      // eslint-disable-line react/prop-types
      bodyRowComponentProps,
      selectionType
    } = props;
    const selectionTypeRow = selectionType === ROW_SELECTION;
    const isSelected = selectedRows.indexOf(extraProps.itemKey) >= 0;
    const isDisabled = disabledRows.indexOf(extraProps.itemKey) >= 0;
    const selectedRowStyle = isSelected && selectableCSSStyles.selectedRowStyle;
    const disabledRowStyle = isDisabled && selectableCSSStyles.disabledRowStyle;
    const selectionTypeStyles = selectionTypeRow ? selectableRowStyle : disabledRowStyle || selectedRowStyle;
    const rowComponentProps = bodyRowComponentProps && typeof bodyRowComponentProps === "function" ? bodyRowComponentProps(_objectSpread(_objectSpread({}, extraProps), {}, {
      css: selectionTypeStyles
    })) : {
      css: selectionTypeStyles
    };
    const returnObject = selectionTypeRow ? _objectSpread(_objectSpread({
      rowComponent: bodyRowComponent || "tr"
    }, rowComponentProps), {}, {
      isSelected: selectedRows.indexOf(extraProps.itemKey) >= 0,
      onSelection: handleSelection
    }) : _objectSpread({}, rowComponentProps);
    return _objectSpread({}, returnObject);
  };
  /**
   * Returns Checkbox or Radiobutton to each row, based on `props.selectionType`.
   *
   * @param {Object} rowData
   * @param {Object} rowData.item Row's item data
   * @param {Object} rowData.itemKey Row's item id
   */


  const selectableType = _ref => {
    let {
      item,
      itemKey
    } = _ref;
    const isSelected = selectedRows.indexOf(itemKey) >= 0;
    const singleSelectionType = selectionType === SINGLE_SELECTION;
    return _jsx(SelectionControl, {
      "aria-label": getA11YLabel(item, itemKey, isSelected),
      name: selectedCheckbox,
      id: itemKey,
      value: itemKey,
      checked: isSelected,
      disabled: disabledRows.indexOf(itemKey) >= 0,
      selectionType: selectionType,
      onValueChange: handleSelection,
      selectionCSS: singleSelectionType ? _objectSpread(_objectSpread({}, selectionTypeStyle), singleSelectionStyle) : selectionTypeStyle
    });
  };
  /**
   * This method is used to determine the columns based on `selectionType`.
   * @param {Array} originalColumns Array of columns.
   * @param {Boolean} selectableTypeRow Will be `true` if the selection mode is `row`.
   * @param {String} selectionType Selection type (multi, single or row).
   * @param {String} headerClassName Header class names.
   * @return {Array} Array of columns based on `selectionType`.
   */


  function getColumns(_ref2) {
    let {
      originalColumns,
      selectableTypeRow,
      selectionType,
      headerClassName,
      totalSelectedRows
    } = _ref2;
    const columns = selectableTypeRow ?
    /**
     * selectionType="row" does not return a component, as its functionality
     * is represented by a combination of events and styles applied to the
     * table row when selectionType="row".
     */
    originalColumns : [{
      key: SELECTABLE_COLUMN_KEY,
      className: headerClassName,
      header: getHeader(totalSelectedRows, selectedValue),
      headerCellComponentProps: {
        css: selectableStyle
      },
      headerCellComponent: selectionType === SINGLE_SELECTION ? "th" : "td",
      bodyCellComponentProps: {
        css: selectableStyle
      },
      formatter: selectableType
    }, ...originalColumns];

    if (selectableTypeRow) {
      const totalColumns = columns.length;

      for (let i = 0; i < totalColumns; i++) {
        const column = columns[i];
        const {
          bodyCellComponentProps,
          headerCellComponentProps
        } = column;
        bodyCellComponentProps ? bodyCellComponentProps.role = "gridcell" : column.bodyCellComponentProps = {
          role: "gridcell"
        };
        headerCellComponentProps ? headerCellComponentProps.id = `${headerColumnId}${i}` : column.headerCellComponentProps = {
          id: `${headerColumnId}${i}`
        };
      }
    }

    return columns;
  }
  /**
   * This method returns the count of total selected rows.
   * @return {Number} Count of selected rows.
   */


  const getTotalSelectedRows = () => {
    const enabledSelectedRows = getEnabledSelectedRows();
    return totalRows === data.length ? getExistingRowsCount(data, getRowKeyValue, enabledSelectedRows) : enabledSelectedRows.length;
  };
  /**
   * This method is used to determine if a row is selected or disabled, and
   * in response returns a className String.
   *
   * @param {Object} row
   * @param {String} row.item The item data supplied to this row.
   * @param {Number} row.itemIndex The row index.
   * @param {String} row.itemKey The unique id of the row, as defined by `props.rowKey`
   * @return {String} A string representing the class or space-separated classes to add to the row.
   */


  const setRowClassName = row => {
    return clsx(SELECTABLE_ROW_CLASS, {
      "pnr-table__row--selected": selectedRows.indexOf(row.itemKey) >= 0,
      "pnr-table__row--disabled": disabledRows.indexOf(row.itemKey) >= 0
    }, resultOf(rowClassName, row, this));
  };

  const {
    columns: originalColumns
  } = tableProps;
  const selectableTypeRow = selectionType === ROW_SELECTION && data.length > 0;
  const totalSelectedRows = getTotalSelectedRows();
  /**
   * When SelectAll is used, it is important to ensure that the proper
   * menubutton selections are rendered on each page.
   */

  if (selectAll && paginatedData.length && (selectedValue !== "" || getEnabledSelectedRows().length > 0 || totalSelectedRows > 0)) {
    const totalSelectedRowsOnPage = getRowsSelectedOnPage().length;
    const totalAvailableRows = paginatedData.length - getDisabledRows(paginatedData);
    selectedValue = inferSelectAllState(totalSelectedRowsOnPage, totalAvailableRows, totalSelectedRows, totalRows - disabledRows.length);
  }

  const headerClassName = clsx("pnr-table__selectable", {
    // The column head is empty and rows include checkboxes.
    "pnr-table__selectable-checkbox": !selectAll && !paginatedData.length && selectionType !== SINGLE_SELECTION,
    // The column head is empty and rows include radiobuttons.
    "pnr--table__selectable-single": selectionType === SINGLE_SELECTION,
    // A select-all component is added to the column head; rows include checkboxes.
    "pnr-table__selectable-selectall": paginatedData.length || selectAll
  });
  const columns = getColumns({
    originalColumns,
    selectableTypeRow,
    selectionType,
    headerClassName,
    totalSelectedRows
  });

  if (selectableTypeRow) {
    return _jsx(TableComponent, _objectSpread(_objectSpread({}, tableProps), {}, {
      selectedRows: selectedRows,
      selectableTypeRow: true,
      data: data,
      columns: columns,
      bodyRowComponent: SelectableRow,
      bodyRowComponentProps: getBodyRowComponentProps,
      rowClassName: `${SELECTABLE_ROW_CLASS} pnr-table__selection-type-row`,
      ref: tableRef
    }));
  } else {
    return _jsxs(React.Fragment, {
      children: [_jsx(LiveRegion, {
        politeness: "polite",
        clearAnnouncementDelay: CLEAR_ANNOUNCEMENT_TIMEOUT,
        children: liveRegionText
      }), _jsx(TableComponent, _objectSpread(_objectSpread({}, tableProps), {}, {
        selectedRows: selectedRows,
        css: selectableCSSStyles,
        data: data,
        paginatedData: paginatedData,
        columns: columns,
        bodyRowComponentProps: getBodyRowComponentProps,
        rowClassName: setRowClassName,
        ref: tableRef
      }))]
    });
  }
});
Selectable.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * A function that generates a unique key for each items,
   * or a string that identifies the unique key among the items.
   * For more information, check https://facebook.github.io/react/docs/lists-and-keys.html#keys.
   */
  rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,

  /**
   * The Table component to be extended.
   */
  tableComponent: PropTypes.object.isRequired,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Function that allows Selectable to announce some live text for a11y
   */
  announceLiveText: PropTypes.func,

  /**
   * The extra props passed to the bodyRowComponent or a function that will be called by each row,
   * to get one or more object properties, which will be appended to the row. `fn(row, rowIndex)`.
   */
  bodyRowComponentProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),

  /**
   * Override for strings used within the component.
   */
  customStrings: PropTypes.shape({
    // Used for text 'unselected' on deslecting a row.
    deselected: PropTypes.string,

    /**
     * Used for overriding default textContent ``${selectedRowsCount} rows ${status}``
     * of visually hidden node.
     */
    getSelectAllStatus: PropTypes.func,
    // Used for text 'Row'.
    row: PropTypes.string,
    // Used for text 'Select All'.
    selectAll: PropTypes.string,
    // Used for text 'selected' on selecting a row.
    selected: PropTypes.string,
    // Used for text 'Select'.
    select: PropTypes.string,

    /**
     * String used for the option to unselect all items.
     * Default: "Unselect all items".
     */
    unselectAllItems: PropTypes.string,

    /**
     * String used for the option to unselect all items on the page.
     * Default: "Unselect all on this page".
     */
    unselectAllOnThisPage: PropTypes.string,

    /**
     * String used in the first option along with count of the selected items.
     * Default: "items selected".
     */
    itemsSelected: PropTypes.string,

    /**
     * String used for the option to select all items.
     * Default: "Select all items".
     */
    selectAllItems: PropTypes.string,

    /**
     * String used for the option to select all items on the page.
     * Default: "Select all on this page".
     */
    selectAllOnThisPage: PropTypes.string,

    /**
     * String used in the `aria-label` of the select all button.
     * Default: "Select All,".
     */
    selectAllButton: PropTypes.string
  }),

  /** Data provided to the table. */
  data: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /** Array of ids to disable. */
  disabledRows: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /**
   * A callback function that fires whenever there's an across pages
   * selection (clicking the "Select/Unselect all items (x)" menu items).
   * It receives a boolean indicating if it's a select or unselect.
   */
  onSelectedAllChange: PropTypes.func,

  /**
   * A callback function that fires whenever a Table selection is made.
   * For an uncontrolled Table with a SelectAll menu, `onSelectedChange` will fire for all menu options.
   * For a controlled Table with a SelectAll menu, `onSelectedChange` will fire
   * for "Select/Unselect all on this page" menu items.
   * It receives an object containing an array of selected ids.
   */
  onSelectedChange: PropTypes.func,

  /**
   * Applicable only in pagination scenarios.
   * When both paginatedData and data are present, paginatedData takes precedence.
   * It is used only to render table rows in pagination scenarios.
   */
  paginatedData: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-line react/forbid-prop-types

  /**
   * Determines whether the state of the `selectable` needs to be maintained by the Table
   * when `data` prop changes.
   * Applicable only when `controlled` is not set to true.
   */
  retainStateOnDataChange: PropTypes.bool,

  /**
   * Render prop for the Checkbox/Radio aria-label
   * @param {Object} rowData
   * @param {Object} rowData.item Row's item data
   * @param {Boolean} rowData.selected Row's selected state
   */
  rowAriaLabel: PropTypes.func,

  /**
   * When `selectAll` is set to true, the appropriate selection component will be added to the
   * column head: either a `Checkbox` when all data is shown in a single page, or a `SelectAll`
   * component when a paginator is present. Setting `selectAll` to `false` will not render any
   * selection component to the column head.
   */
  selectAll: PropTypes.bool,

  /**
   * `selectAllConfiguration` is an optional configuration object that can be configured when
   * `selectable.selectAll` is set to `true` and custom control over toggling all items
   * on the page is needed.
   * <br /><br />
   * <strong>Example configuration</strong>
   *
   * ```javascript
   * selectable={{
   *   selectAll: true,
   *   selectAllConfiguration: {
   *   // Pagination exists and a Checkbox is used for select-all.
   *     selectAllType: "checkbox"
   *   }
   *   ...
   * ```
   */
  selectAllConfiguration: PropTypes.shape({
    /**
     * `selectAllType` is a configuration prop that sets the `selectAll` control in table header
     * to a `Checkbox` or a `MenuButton`. Accepts values `auto` and `checkbox`. Defaults to `auto`.
     */
    selectAllType: PropTypes.oneOf(["auto", "checkbox"])
  }),

  /** An array of `id` values that indicate the corresponding data set is selected. */
  selectedRows: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Indicates how the row item can be selected. `multi` adds checkboxes,
   * `single` adds radiobuttons, and `row` add the selectable row feature.
   */
  selectionType: PropTypes.oneOf(["multi", "single", "row"]),

  /**
   * Provides the total rows available across the data set.
   */
  totalRows: PropTypes.number
} : {};
Selectable.defaultProps = {
  announceLiveText: null,
  bodyRowComponentProps: null,
  customStrings: {},
  data: [],
  disabledRows: [],
  onSelectedAllChange: null,
  onSelectedChange: null,
  retainStateOnDataChange: false,
  rowAriaLabel: null,
  selectAll: false,
  selectedRows: [],
  selectionType: null,
  selectAllConfiguration: null,
  totalRows: null,
  paginatedData: []
};
/**
 * This is the actual HOC that will be exported. It returns a Table component
 * wrapped with the Selectable component defined above.
 */

function withSelectable(TableComponent) {
  const WrappedTable = /*#__PURE__*/React.forwardRef((props, ref) => {
    const {
      selectable,
      data,
      retainStateOnDataChange
    } = props,
          others = _objectWithoutProperties(props, _excluded2);

    if (selectable && data && data.length > 0) {
      let rowSelectionProps = {};

      if (selectable.selectionType === ROW_SELECTION) {
        rowSelectionProps = {
          role: "grid",
          "aria-readonly": true,
          "aria-multiselectable": true
        };
      }

      return _jsx(Selectable, _objectSpread(_objectSpread(_objectSpread(_objectSpread({
        data: data
      }, rowSelectionProps), selectable), others), {}, {
        ref: ref,
        retainStateOnDataChange: retainStateOnDataChange && retainStateOnDataChange.indexOf("selectable") !== -1,
        tableComponent: TableComponent
      }));
    } else {
      return _jsx(TableComponent, _objectSpread(_objectSpread({
        data: data
      }, others), {}, {
        ref: ref
      }));
    }
  });
  WrappedTable.displayName = `withSelectable(${TableComponent.displayName || TableComponent.name})`;
  return WrappedTable;
}

export default withSelectable;